import { ImageUpload } from "@/components/admin/ImageUpload";
import { VideoUpload } from "@/components/admin/VideoUpload";
import { HIGHLIGHT_ICONS, AMENITIES, DAYS_EL } from "@/constants/businessMeta";
import { Plus, Trash2 } from "lucide-react";

const I = ({ label, value, onChange, placeholder, type = "text" }) => (
  <div>
    {label && <label className="block text-xs font-semibold mb-1 text-slate-600">{label}</label>}
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full h-9 px-3 rounded-lg bg-muted text-sm outline-none focus:ring-2 ring-primary" />
  </div>
);

const Block = ({ title, children }) => (
  <div className="rounded-xl border border-border p-3 space-y-3">
    <h4 className="font-bold text-sm text-slate-800">{title}</h4>
    {children}
  </div>
);

export const BusinessExtraFields = ({ biz, set }) => {
  const arr = (k) => biz[k] || [];
  const setArr = (k, v) => set({ [k]: v });
  const addTo = (k, item) => setArr(k, [...arr(k), item]);
  const rmFrom = (k, i) => setArr(k, arr(k).filter((_, idx) => idx !== i));
  const updAt = (k, i, patch) => setArr(k, arr(k).map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  // opening hours initialise to 7 days
  const hours = arr("opening_hours").length ? biz.opening_hours : DAYS_EL.map((d) => ({ day: d, open: "", close: "", closed: false }));
  const setHour = (i, patch) => setArr("opening_hours", hours.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));

  const amenities = biz.amenities || [];
  const toggleAmenity = (key) => setArr("amenities", amenities.includes(key) ? amenities.filter((a) => a !== key) : [...amenities, key]);

  const social = biz.social || {};
  const setSocial = (k, v) => set({ social: { ...social, [k]: v } });

  const tagsStr = (biz.tags || []).join(", ");

  return (
    <div className="space-y-4" data-testid="business-extra-fields">
      <ImageUpload label="Cover image (hero)" value={biz.cover_image} onChange={(v) => set({ cover_image: v })} />

      <div className="grid sm:grid-cols-2 gap-3">
        <VideoUpload value={biz.video_url} onChange={(v) => set({ video_url: v })} />
        <I label="Tags (χωρισμένα με κόμμα)" value={tagsStr} onChange={(v) => set({ tags: v.split(",").map((t) => t.trim()).filter(Boolean) })} placeholder="Παιδιά, Έφηβοι, Ενήλικες" />
      </div>

      <div>
        <label className="block text-xs font-semibold mb-1 text-slate-600">Τι μας ξεχωρίζει</label>
        <textarea rows={4} value={biz.about || ""} onChange={(e) => set({ about: e.target.value })} data-testid="biz-about-input"
          className="w-full px-3 py-2 rounded-lg bg-muted text-sm outline-none focus:ring-2 ring-primary resize-none" />
      </div>

      <label className="flex items-center gap-2 text-sm font-semibold">
        <input type="checkbox" checked={!!biz.verified} onChange={(e) => set({ verified: e.target.checked })} data-testid="biz-verified-checkbox" className="h-4 w-4" />
        ✓ Επιβεβαιωμένη επιχείρηση (Verified)
      </label>

      {/* Highlights */}
      <Block title="Κάρτες χαρακτηριστικών">
        {arr("highlights").map((h, i) => (
          <div key={i} className="flex gap-2 items-start" data-testid={`highlight-row-${i}`}>
            <select value={h.icon || "info"} onChange={(e) => updAt("highlights", i, { icon: e.target.value })} className="h-9 px-2 rounded-lg bg-muted text-sm shrink-0">
              {HIGHLIGHT_ICONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            <div className="flex-1"><I value={h.title} onChange={(v) => updAt("highlights", i, { title: v })} placeholder="Τίτλος (π.χ. Ηλικίες)" /></div>
            <div className="flex-1"><I value={h.value} onChange={(v) => updAt("highlights", i, { value: v })} placeholder="Πληροφορία" /></div>
            <button type="button" onClick={() => rmFrom("highlights", i)} className="h-9 w-9 grid place-items-center text-destructive"><Trash2 size={15} /></button>
          </div>
        ))}
        <button type="button" onClick={() => addTo("highlights", { icon: "info", title: "", value: "" })} data-testid="add-highlight" className="text-sm font-semibold text-primary flex items-center gap-1"><Plus size={14} /> Προσθήκη</button>
      </Block>

      {/* Departments */}
      <Block title="Τμήματα / Υπηρεσίες">
        {arr("departments").map((d, i) => (
          <div key={i} className="rounded-lg border border-border p-2 space-y-2" data-testid={`department-row-${i}`}>
            <div className="flex justify-between items-center">
              <span className="text-xs font-bold text-slate-500">Τμήμα {i + 1}</span>
              <button type="button" onClick={() => rmFrom("departments", i)} className="text-destructive"><Trash2 size={15} /></button>
            </div>
            <I value={d.name} onChange={(v) => updAt("departments", i, { name: v })} placeholder="Όνομα τμήματος" />
            <ImageUpload label="Φωτογραφία τμήματος" value={d.image} onChange={(v) => updAt("departments", i, { image: v })} />
            <textarea rows={2} value={d.description || ""} onChange={(e) => updAt("departments", i, { description: e.target.value })} placeholder="Σύντομη περιγραφή" className="w-full px-3 py-2 rounded-lg bg-muted text-sm outline-none resize-none" />
            <div className="grid grid-cols-2 gap-2">
              <I value={d.ages} onChange={(v) => updAt("departments", i, { ages: v })} placeholder="Ηλικίες (4–8)" />
              <I value={d.level} onChange={(v) => updAt("departments", i, { level: v })} placeholder="Επίπεδο" />
              <I value={d.days} onChange={(v) => updAt("departments", i, { days: v })} placeholder="Ημέρες" />
              <I value={d.hours} onChange={(v) => updAt("departments", i, { hours: v })} placeholder="Ώρες" />
              <I value={d.location} onChange={(v) => updAt("departments", i, { location: v })} placeholder="Τοποθεσία" />
              <I value={d.cost} onChange={(v) => updAt("departments", i, { cost: v })} placeholder="Κόστος (προαιρετικό)" />
            </div>
            <I value={d.slots} onChange={(v) => updAt("departments", i, { slots: v })} placeholder="Διαθέσιμες θέσεις (προαιρετικό)" />
          </div>
        ))}
        <button type="button" onClick={() => addTo("departments", { name: "", image: "", description: "", ages: "", level: "", days: "", hours: "", location: "", cost: "", slots: "" })} data-testid="add-department" className="text-sm font-semibold text-primary flex items-center gap-1"><Plus size={14} /> Προσθήκη τμήματος</button>
      </Block>

      {/* Opening hours */}
      <Block title="Ωράριο λειτουργίας">
        {hours.map((h, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`hours-row-${i}`}>
            <span className="w-24 text-sm text-slate-600">{h.day}</span>
            <input type="time" value={h.open || ""} disabled={h.closed} onChange={(e) => setHour(i, { open: e.target.value })} className="h-9 px-2 rounded-lg bg-muted text-sm disabled:opacity-40" />
            <span>–</span>
            <input type="time" value={h.close || ""} disabled={h.closed} onChange={(e) => setHour(i, { close: e.target.value })} className="h-9 px-2 rounded-lg bg-muted text-sm disabled:opacity-40" />
            <label className="flex items-center gap-1 text-xs ml-auto"><input type="checkbox" checked={!!h.closed} onChange={(e) => setHour(i, { closed: e.target.checked })} /> Κλειστά</label>
          </div>
        ))}
      </Block>

      {/* Facilities (venues with photos) */}
      <Block title="Εγκαταστάσεις (με φωτογραφίες)">
        {arr("facilities").map((f, i) => (
          <div key={i} className="rounded-lg border border-border p-2 space-y-2" data-testid={`facility-row-${i}`}>
            <div className="flex justify-between items-center"><span className="text-xs font-bold text-slate-500">Εγκατάσταση {i + 1}</span><button type="button" onClick={() => rmFrom("facilities", i)} className="text-destructive"><Trash2 size={15} /></button></div>
            <I value={f.label} onChange={(v) => updAt("facilities", i, { label: v })} placeholder="Όνομα (π.χ. Κύριο γήπεδο)" />
            <I value={f.note} onChange={(v) => updAt("facilities", i, { note: v })} placeholder="Σημείωση" />
            <ImageUpload label="Φωτογραφία" value={f.image} onChange={(v) => updAt("facilities", i, { image: v })} />
          </div>
        ))}
        <button type="button" onClick={() => addTo("facilities", { label: "", image: "", note: "" })} data-testid="add-facility" className="text-sm font-semibold text-primary flex items-center gap-1"><Plus size={14} /> Προσθήκη εγκατάστασης</button>
      </Block>

      {/* Amenities checklist */}
      <Block title="Παροχές">
        <div className="grid grid-cols-2 gap-1.5">
          {AMENITIES.map((a) => (
            <label key={a.key} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={amenities.includes(a.key)} onChange={() => toggleAmenity(a.key)} data-testid={`amenity-${a.key}`} /> {a.label}
            </label>
          ))}
        </div>
      </Block>

      {/* Social */}
      <Block title="Social Media">
        <div className="grid sm:grid-cols-2 gap-2">
          {["facebook", "instagram", "tiktok", "youtube", "linkedin"].map((k) => (
            <I key={k} label={k.charAt(0).toUpperCase() + k.slice(1)} value={social[k]} onChange={(v) => setSocial(k, v)} placeholder="https://..." />
          ))}
        </div>
      </Block>
    </div>
  );
};
